import { Texture } from "pixi.js";

// Texturas geradas uma única vez (offscreen canvas) e usadas como sprites.
// Esferas/glow são brancas → recebem tint da cor da galáxia em runtime.

function cv(size: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement("canvas");
  c.width = c.height = size;
  return [c, c.getContext("2d")!];
}

/** halo de glow radial branco (tintável) */
function glowTexture(size = 256): Texture {
  const [c, ctx] = cv(size);
  const r = size / 2;
  const g = ctx.createRadialGradient(r, r, 0, r, r, r);
  g.addColorStop(0, "rgba(255,255,255,0.9)");
  g.addColorStop(0.4, "rgba(255,255,255,0.25)");
  g.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  return Texture.from(c);
}

/** esfera shaded com highlight deslocado (tintável) */
function sphereTexture(size = 256): Texture {
  const [c, ctx] = cv(size);
  const r = size / 2;
  const g = ctx.createRadialGradient(r * 0.66, r * 0.6, r * 0.05, r, r, r);
  g.addColorStop(0, "rgba(255,255,255,1)");
  g.addColorStop(0.28, "rgba(245,245,245,1)");
  g.addColorStop(0.6, "rgba(160,160,160,1)");
  g.addColorStop(0.92, "rgba(70,70,70,1)");
  g.addColorStop(1, "rgba(40,40,40,0)");
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(r, r, r * 0.98, 0, Math.PI * 2);
  ctx.fill();
  return Texture.from(c);
}

/** highlight especular branco (não tintado) */
function specTexture(size = 128): Texture {
  const [c, ctx] = cv(size);
  const r = size / 2;
  const g = ctx.createRadialGradient(r, r, 0, r, r, r);
  g.addColorStop(0, "rgba(255,255,255,0.95)");
  g.addColorStop(0.5, "rgba(255,255,255,0.25)");
  g.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  return Texture.from(c);
}

/** núcleo do Sol — dourado branco-quente (não tintado) */
function sunTexture(size = 512): Texture {
  const [c, ctx] = cv(size);
  const r = size / 2;
  const g = ctx.createRadialGradient(r * 0.64, r * 0.58, r * 0.04, r, r, r);
  g.addColorStop(0, "#FFFFFF");
  g.addColorStop(0.26, "#FFF3C0");
  g.addColorStop(0.62, "#FFD700");
  g.addColorStop(0.93, "#E29400");
  g.addColorStop(1, "rgba(226,148,0,0)");
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(r, r, r * 0.98, 0, Math.PI * 2);
  ctx.fill();
  return Texture.from(c);
}

/** coroa solar — raios (gira em runtime) */
function coronaTexture(size = 1024): Texture {
  const [c, ctx] = cv(size);
  const r = size / 2;
  const rays = 18;
  ctx.translate(r, r);
  for (let i = 0; i < rays; i++) {
    ctx.rotate((Math.PI * 2) / rays);
    const g = ctx.createLinearGradient(0, 0, 0, -r);
    g.addColorStop(0, "rgba(255,230,128,0.16)");
    g.addColorStop(1, "rgba(255,230,128,0)");
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.moveTo(-r * 0.03, 0);
    ctx.lineTo(r * 0.03, 0);
    ctx.lineTo(0, -r);
    ctx.closePath();
    ctx.fill();
  }
  return Texture.from(c);
}

/** halo translúcido dourado (anel) */
function haloTexture(size = 512): Texture {
  const [c, ctx] = cv(size);
  const r = size / 2;
  const g = ctx.createRadialGradient(r, r, r * 0.62, r, r, r);
  g.addColorStop(0, "rgba(255,215,0,0)");
  g.addColorStop(0.55, "rgba(255,210,74,0.5)");
  g.addColorStop(0.8, "rgba(255,192,32,0.22)");
  g.addColorStop(1, "rgba(255,179,0,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  return Texture.from(c);
}

export interface CosmosTextures {
  glow: Texture;
  sphere: Texture;
  spec: Texture;
  sun: Texture;
  corona: Texture;
  halo: Texture;
}

let cache: CosmosTextures | null = null;
export function getTextures(): CosmosTextures {
  if (cache) return cache;
  cache = {
    glow: glowTexture(),
    sphere: sphereTexture(),
    spec: specTexture(),
    sun: sunTexture(),
    corona: coronaTexture(),
    halo: haloTexture(),
  };
  return cache;
}
